import { expectedReturn, hitProbability, sideProbability, evensProbability } from './math';

/**
 * Paytables, expressed as a multiplier of the per-draw stake for a given number
 * of hits. Index = hits. Shape follows the Keno Australia "Keno Classic"
 * game guide (Nov 2025); top prizes for 7-10 spots are fixed here rather than
 * progressive jackpots, and figures are tuned so each spot returns close to
 * 80% (the same return Keno Australia gives Heads or Tails). These are the
 * defaults; the live values are read from Firestore `config/paytable` so
 * they can be tuned without a deploy.
 */
export type SpotPaytable = Record<number, number>; // hits -> multiplier

export const DEFAULT_SPOT_PAYTABLE: Record<number, SpotPaytable> = {
  1: { 1: 3.2 },
  2: { 2: 13.3 },
  3: { 2: 1, 3: 47 },
  4: { 2: 1, 3: 4, 4: 135 },
  5: { 3: 2, 4: 13, 5: 750 },
  6: { 3: 1, 4: 6, 5: 75, 6: 2000 },
  7: { 3: 1, 4: 3, 5: 16, 6: 180, 7: 8000 },
  8: { 4: 2, 5: 8, 6: 80, 7: 1000, 8: 30000 },
  9: { 4: 1, 5: 6, 6: 25, 7: 280, 8: 3000, 9: 100000 },
  10: { 4: 1, 5: 2, 6: 8, 7: 80, 8: 1000, 9: 12000, 10: 1000000 },
};

/** Heads or Tails: same as Keno Australia. 11+ in your half pays 2x, exactly 10/10 pays 4x. */
export const DEFAULT_HEADS_TAILS_PAYTABLE = { side: 2, evens: 4 };

export interface Paytable {
  spots: Record<number, SpotPaytable>;
  headsTails: { side: number; evens: number };
}

export const DEFAULT_PAYTABLE: Paytable = {
  spots: DEFAULT_SPOT_PAYTABLE,
  headsTails: DEFAULT_HEADS_TAILS_PAYTABLE,
};

export const SPOT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export function spotMultiplier(paytable: Paytable, spots: number, hits: number): number {
  return paytable.spots[spots]?.[hits] ?? 0;
}

export function asArray(table: SpotPaytable, spots: number): number[] {
  const out: number[] = [];
  for (let h = 0; h <= spots; h++) out.push(table[h] ?? 0);
  return out;
}

export interface SpotStats {
  spots: number;
  rtp: number;
  anyPrizeOdds: number;
  topPrizeOdds: number;
  rows: { hits: number; multiplier: number; probability: number; odds: number }[];
}

export function spotStats(paytable: Paytable, spots: number): SpotStats {
  const table = paytable.spots[spots] ?? {};
  const arr = asArray(table, spots);
  const rows = [];
  let pAny = 0;
  for (let h = spots; h >= 0; h--) {
    const multiplier = arr[h];
    if (!multiplier) continue;
    const probability = hitProbability(spots, h);
    pAny += probability;
    rows.push({ hits: h, multiplier, probability, odds: 1 / probability });
  }
  return {
    spots,
    rtp: expectedReturn(spots, arr),
    anyPrizeOdds: pAny ? 1 / pAny : Infinity,
    topPrizeOdds: 1 / hitProbability(spots, spots),
    rows,
  };
}

export function headsTailsStats(paytable: Paytable) {
  const pSide = sideProbability();
  const pEvens = evensProbability();
  return {
    side: { multiplier: paytable.headsTails.side, probability: pSide, rtp: pSide * paytable.headsTails.side },
    evens: { multiplier: paytable.headsTails.evens, probability: pEvens, rtp: pEvens * paytable.headsTails.evens },
  };
}
